import React from 'react'
import { ActivityIndicator, Platform, RefreshControl, ScrollView, StyleSheet, View } from 'react-native'
import { useNavigation, useTheme } from '@react-navigation/native'
import type { NativeStackScreenProps } from '@react-navigation/native-stack'
import { Button, Header, Screen, Text } from '@components'
import { useLanguage } from '@context'
import type { AppNavigationProp } from '@navigation'
import { Theme } from '@theme'
import { eventAccessProblem, formatEventPrice } from '@utils'
import {
  useEventById,
  useEventInvitations,
  useEventsPullToRefresh,
  useOrganizerAttendees,
  useRefreshEventsOnFocus,
  useScreen,
} from '@hooks'
import type { EventsStackParamList } from '../../../navigation/navigator/EventsStackNavigator'
import { EventHero } from '../../events/components/EventHero'
import { AccessDenied } from '../components/AccessDenied'
import { AttendeesPanel } from './components/AttendeesPanel'
import { EmailingPanel } from './components/EmailingPanel'
import { InvitePanel } from './components/InvitePanel'
import { RemindersToggle } from './components/RemindersToggle'
import { SharePanel } from './components/SharePanel'

type Props = NativeStackScreenProps<EventsStackParamList, 'EventManage'>

/** Organizer dashboard of one event: numbers, attendees CRM, sharing and invitations. */
export const EventManage: React.FC<Props> = ({ route, navigation }) => {
  const theme = useTheme()
  const { t, currentLanguage } = useLanguage()
  const rootNavigation = useNavigation<AppNavigationProp>()
  const { isDesktop } = useScreen()
  const styles = getStyles(theme, isDesktop)
  const { eventId } = route.params
  const { data, isLoading, error } = useEventById(eventId)
  const counts = useOrganizerAttendees(eventId, '', 'all')
  const invitations = useEventInvitations(eventId)
  const { refreshing, onRefresh } = useEventsPullToRefresh()
  useRefreshEventsOnFocus()

  const accessProblem = eventAccessProblem(data, error)
  if (accessProblem) {
    return (
      <AccessDenied
        message={t(`events.access.${accessProblem === 'notFound' ? 'notFound' : 'forbidden'}`)}
        onBack={() => navigation.navigate('EventsList')}
      />
    )
  }

  if (isLoading || !data) {
    return (
      <Screen>
        <ActivityIndicator color={theme.colors.primary} />
      </Screen>
    )
  }

  const { event, organizer } = data
  const totals = counts.data?.metadata.totals
  const stats: { label: string; value: string; color: string; hint?: string; testID?: string }[] = [
    {
      label: t('events.manage.stats.registered'),
      value:
        event.capacity !== null
          ? `${event.attendeeCount} / ${event.capacity}`
          : String(event.attendeeCount),
      color: theme.colors.primary,
    },
    {
      label: t('events.manage.stats.present'),
      value: String(totals?.checkedIn ?? 0),
      color: theme.colors.green,
    },
    {
      label: t('events.manage.stats.attendanceRate'),
      value: `${counts.data?.metadata.stats?.attendanceRate ?? 0}%`,
      color: theme.colors.green,
      testID: 'stat-attendance-rate',
    },
    {
      label: t('events.manage.stats.invitations'),
      value: String(invitations.data?.metadata.count ?? 0),
      hint: t('events.manage.stats.accepted', { count: invitations.data?.metadata.accepted ?? 0 }),
      color: theme.colors.orange,
    },
    {
      label: t('events.manage.stats.contactsExchanged'),
      value: String(counts.data?.metadata.stats?.contactsExchanged ?? 0),
      color: theme.colors.secondary,
      testID: 'stat-contacts-exchanged',
    },
    {
      label: t('events.manage.stats.followupsSent'),
      value: String(counts.data?.metadata.stats?.followupsSent ?? 0),
      color: theme.colors.orange,
      testID: 'stat-followups-sent',
    },
  ]

  if (event.priceCents !== null) {
    stats.push({
      label: t('events.manage.stats.collected'),
      value: formatEventPrice(data.sales?.totalCents ?? 0, currentLanguage, event.currency),
      hint: t('events.manage.stats.paidCount', { count: data.sales?.paidCount ?? 0 }),
      color: theme.colors.green,
      testID: 'stat-collected',
    })
    if (data.sales) {
      stats.push({
        label: t('events.manage.stats.commission'),
        value: formatEventPrice(data.sales.commissionCents, currentLanguage, event.currency),
        color: theme.colors.orange,
        testID: 'stat-commission',
      })
      stats.push({
        label: t('events.manage.stats.net'),
        value: formatEventPrice(data.sales.organizerNetCents, currentLanguage, event.currency),
        hint: t('events.manage.stats.netHint'),
        color: theme.colors.green,
        testID: 'stat-net',
      })
    }
  }

  return (
    <Screen>
      <Header
        title={event.name}
        iconLeft={Platform.OS === 'web' ? 'arrowLeft' : 'arrowBack'}
        onPressLeft={() => navigation.navigate('EventsList')}
      />
      <ScrollView
        contentContainerStyle={styles.scroll}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={theme.colors.primary}
          />
        }
      >
        <View style={styles.content}>
          <View style={styles.topActions}>
            <Button
              title={t('events.manage.edit')}
              iconName="edit"
              isSmall
              unfilled
              onPress={() => navigation.navigate('EventEdit', { eventId })}
            />
            <Button
              title={t('events.manage.viewPage')}
              iconName="openInNew"
              isSmall
              unfilled
              onPress={() => rootNavigation.navigate('PublicEvent', { code: event.code })}
            />
          </View>

          <View style={styles.checkInBanner}>
            <View style={styles.checkInText}>
              <Text type="bodyBold">{t('events.checkin.startTitle')}</Text>
              <Text type="bodySmall" color={theme.colors.greyBlue}>
                {t('events.checkin.startHint')}
              </Text>
            </View>
            <Button
              title={t('events.checkin.start')}
              iconName="check"
              testID="start-checkin"
              onPress={() => navigation.navigate('EventCheckIn', { eventId })}
            />
          </View>

          <View style={styles.stats}>
            {stats.map((stat) => (
              <View key={stat.label} style={styles.stat} testID={stat.testID}>
                <Text type="bodySmallBold" color={theme.colors.greyBlue}>
                  {stat.label}
                </Text>
                <Text type="titleBigBold" color={stat.color}>
                  {stat.value}
                </Text>
                {stat.hint ? (
                  <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
                    {stat.hint}
                  </Text>
                ) : null}
              </View>
            ))}
          </View>

          <View style={styles.columns}>
            <View style={styles.main}>
              <EventHero event={event} organizer={organizer} compact />
              <AttendeesPanel eventId={eventId} />
              <EmailingPanel eventId={eventId} />
            </View>
            <View style={styles.side}>
              <SharePanel event={event} />
              <RemindersToggle eventId={eventId} remindersEnabled={event.remindersEnabled} />
              <InvitePanel eventId={eventId} />
            </View>
          </View>
        </View>
      </ScrollView>
    </Screen>
  )
}

const getStyles = (theme: Theme, isDesktop: boolean) =>
  StyleSheet.create({
    scroll: { padding: isDesktop ? theme.spacing.xl : theme.spacing.lg, alignItems: 'center' },
    content: { width: '100%', maxWidth: 1180, gap: theme.spacing.xl },
    topActions: {
      flexDirection: 'row',
      gap: theme.spacing.sm,
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
    },
    checkInBanner: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: theme.spacing.md,
      padding: theme.spacing.lg,
      borderRadius: theme.border.large,
      borderWidth: 1,
      borderColor: theme.colors.primary,
      backgroundColor: theme.colors.sidebar,
    },
    checkInText: { flex: 1, minWidth: 220, gap: 2 },
    stats: { flexDirection: 'row', gap: theme.spacing.md, flexWrap: 'wrap' },
    stat: {
      flex: 1,
      minWidth: 160,
      padding: theme.spacing.lg,
      borderRadius: theme.border.large,
      backgroundColor: theme.colors.white,
      borderWidth: 1,
      borderColor: theme.colors.grey,
      gap: 2,
    },
    columns: {
      flexDirection: isDesktop ? 'row' : 'column',
      gap: theme.spacing.xl,
      alignItems: 'flex-start',
    },
    main: {
      flex: isDesktop ? 1 : undefined,
      width: isDesktop ? undefined : '100%',
      gap: theme.spacing.xl,
      minWidth: 0,
    },
    side: { width: isDesktop ? 360 : '100%', gap: theme.spacing.xl },
  })
